import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { doc, getDoc, setDoc, getDocs, collection, query, where, deleteDoc, updateDoc, Timestamp } from "firebase/firestore";

function getDeviceId() {
  let id = localStorage.getItem("miyabi_device_id");
  if (!id) {
    id = "device_" + Math.random().toString(36).substr(2, 12) + "_" + Date.now();
    localStorage.setItem("miyabi_device_id", id);
  }
  return id;
}

function Toggle({ value, onChange }) {
  return (
    <label style={{ cursor: "pointer" }}>
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} style={{ display: "none" }} />
      <div style={{ width: "48px", height: "24px", borderRadius: "12px", backgroundColor: value ? "#27ae60" : "#ccc", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ position: "absolute", top: "2px", left: "2px", width: "20px", height: "20px", borderRadius: "50%", backgroundColor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "transform 0.2s", transform: value ? "translateX(24px)" : "translateX(0)" }} />
      </div>
    </label>
  );
}

// ===== 通知設定タブ =====
function NotifyTab({ companyId }) {
  const [notifyOn, setNotifyOn] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getDoc(doc(db, "notifySettings", companyId || "default")).then(snap => {
      if (snap.exists()) {
        setNotifyOn(snap.data().notifyOnDocument || false);
        setNotifyEmail(snap.data().notifyEmail || "");
      }
    }).catch(() => setError("設定の読み込みに失敗しました"));
  }, [companyId]);

  const save = async () => {
    setSaving(true); setError("");
    try {
      const id = companyId || "default";
      const existing = await getDoc(doc(db, "notifySettings", id));
      const base = existing.exists() ? existing.data() : {};
      await setDoc(doc(db, "notifySettings", id), {
        ...base, notifyOnDocument: notifyOn, notifyEmail, updatedAt: Timestamp.now(),
      });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { setError("保存に失敗しました"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div style={st.card}>
        <div style={st.cardRow}>
          <div>
            <div style={st.cardTitle}>📧 書類申請メール通知</div>
            <div style={st.cardDesc}>従業員が書類申請を提出したときに指定のメールへ通知します</div>
          </div>
          <Toggle value={notifyOn} onChange={setNotifyOn} />
        </div>
        {notifyOn && (
          <div style={{ marginTop: "16px", borderTop: "1px solid #eee", paddingTop: "16px" }}>
            <label style={st.label}>通知先メールアドレス</label>
            <input style={st.input} type="email" placeholder="admin@example.com"
              value={notifyEmail} onChange={e => setNotifyEmail(e.target.value)} />
            <p style={st.hint}>※ メール送信設定は運営側で管理しています</p>
          </div>
        )}
      </div>
      {error && <p style={{ color: "#e74c3c", fontSize: "13px" }}>{error}</p>}
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button style={st.saveBtn} onClick={save} disabled={saving}>{saving ? "保存中..." : "設定を保存"}</button>
        {saved && <span style={{ color: "#27ae60" }}>✅ 保存しました</span>}
      </div>
    </div>
  );
}

// ===== PC設定タブ =====
function PcTab({ companyId, user }) {
  const [devices, setDevices] = useState([]);
  const [thisDeviceStatus, setThisDeviceStatus] = useState("checking");
  const [registering, setRegistering] = useState(false);
  const deviceId = getDeviceId();

  useEffect(() => { fetchDevices(); }, []);

  const fetchDevices = async () => {
    const q = companyId
      ? query(collection(db, "devices"), where("companyId", "==", companyId))
      : collection(db, "devices");
    const snap = await getDocs(q);
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setDevices(list);
    const mine = list.find(d => d.id === deviceId);
    setThisDeviceStatus(mine ? mine.status : "unregistered");
  };

  const registerThisDevice = async () => {
    setRegistering(true);
    try {
      await setDoc(doc(db, "devices", deviceId), {
        deviceId, companyId: companyId || "", registeredBy: user.uid,
        status: "approved", createdAt: Timestamp.now(),
      });
      localStorage.setItem("miyabi_device_approved", "true");
      localStorage.setItem("miyabi_device_companyId", companyId || "");
      if (companyId) {
        try {
          const snap = await getDoc(doc(db, "companies", companyId));
          if (snap.exists()) {
            localStorage.setItem("miyabi_company_name", snap.data().name || "");
            localStorage.setItem("miyabi_company_logo", snap.data().logo || "");
          }
        } catch {}
      }
      await fetchDevices();
    } catch { alert("登録に失敗しました"); }
    setRegistering(false);
  };

  const removeThisDevice = async () => {
    if (!window.confirm("このPCの登録を解除しますか？ログイン画面のQR表示が消えます。")) return;
    await deleteDoc(doc(db, "devices", deviceId));
    localStorage.removeItem("miyabi_device_approved");
    localStorage.removeItem("miyabi_device_companyId");
    localStorage.removeItem("miyabi_company_name");
    localStorage.removeItem("miyabi_company_logo");
    await fetchDevices();
  };

  const deleteDevice = async (id) => {
    if (!window.confirm("この端末を削除しますか？")) return;
    await deleteDoc(doc(db, "devices", id));
    await fetchDevices();
  };

  const approveDevice = async (id) => {
    await updateDoc(doc(db, "devices", id), { status: "approved" });
    await fetchDevices();
  };

  return (
    <div>
      {/* このPCの設定 */}
      <div style={st.card}>
        <div style={st.cardTitle}>🖥️ このPCの設定</div>
        <div style={st.cardDesc}>ログイン画面にQRコードを表示するには、このPCを打刻端末として登録してください。</div>
        <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "14px", color: thisDeviceStatus === "approved" ? "#27ae60" : "#e67e22", fontWeight: "bold" }}>
            {thisDeviceStatus === "approved" ? "✅ 登録済み（QR表示ON）" : thisDeviceStatus === "checking" ? "確認中..." : "❌ 未登録（QR表示OFF）"}
          </span>
          {thisDeviceStatus !== "approved" && (
            <button style={{ ...st.saveBtn, fontSize: "13px", padding: "8px 20px" }} onClick={registerThisDevice} disabled={registering}>
              {registering ? "登録中..." : "このPCを登録する"}
            </button>
          )}
          {thisDeviceStatus === "approved" && (
            <button style={{ padding: "8px 20px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "8px", cursor: "pointer", fontSize: "13px" }} onClick={removeThisDevice}>
              登録を解除する
            </button>
          )}
        </div>
      </div>

      {/* 端末一覧 */}
      <div style={st.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div style={st.cardTitle}>📋 登録端末一覧</div>
          <button style={{ padding: "6px 14px", backgroundColor: "#f0f4f8", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }} onClick={fetchDevices}>🔄 更新</button>
        </div>
        {devices.length === 0 ? <p style={{ color: "#999", fontSize: "14px" }}>登録された端末はありません</p> : (
          devices.map(d => (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f0f4f8" }}>
              <div>
                <div style={{ fontSize: "13px", color: "#2c3e50", fontFamily: "monospace" }}>{d.id === deviceId ? `${d.deviceId} （このPC）` : d.deviceId}</div>
                <div style={{ fontSize: "12px", color: d.status === "approved" ? "#27ae60" : "#e67e22", marginTop: "2px" }}>
                  {d.status === "approved" ? "✅ 承認済み" : "⏳ 承認待ち"}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                {d.status !== "approved" && (
                  <button style={{ padding: "6px 12px", backgroundColor: "#27ae60", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }} onClick={() => approveDevice(d.id)}>承認</button>
                )}
                <button style={{ padding: "6px 12px", backgroundColor: "#fff", color: "#e74c3c", border: "1px solid #e74c3c", borderRadius: "6px", cursor: "pointer", fontSize: "12px" }} onClick={() => deleteDevice(d.id)}>削除</button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ===== メイン設定ページ =====
function Settings({ companyId, user }) {
  const [tab, setTab] = useState("notify");

  const tabs = [
    { key: "notify", label: "🔔 通知設定" },
    { key: "pc",     label: "🖥️ PC設定" },
  ];

  return (
    <div style={st.container}>
      <h2 style={st.title}>⚙️ 設定</h2>
      <div style={st.tabs}>
        {tabs.map(t => (
          <button key={t.key} style={{ ...st.tab, ...(tab === t.key ? st.activeTab : {}) }} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "notify" && <NotifyTab companyId={companyId} />}
      {tab === "pc"     && <PcTab companyId={companyId} user={user} />}
    </div>
  );
}

const st = {
  container: { padding: "32px", maxWidth: "680px", margin: "0 auto" },
  title: { color: "#2c3e50", marginBottom: "20px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #eee", paddingBottom: "0" },
  tab: { padding: "10px 20px", border: "none", backgroundColor: "transparent", cursor: "pointer", fontSize: "15px", color: "#7f8c8d", borderBottom: "3px solid transparent", marginBottom: "-2px" },
  activeTab: { color: "#2c3e50", fontWeight: "bold", borderBottom: "3px solid #2c3e50" },
  card: { backgroundColor: "#fff", padding: "24px", borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", marginBottom: "16px" },
  cardRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" },
  cardTitle: { fontSize: "15px", fontWeight: "bold", color: "#2c3e50", marginBottom: "6px" },
  cardDesc: { fontSize: "13px", color: "#7f8c8d", lineHeight: "1.5" },
  label: { display: "block", marginBottom: "6px", color: "#2c3e50", fontSize: "14px", fontWeight: "bold" },
  input: { width: "100%", padding: "11px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box", marginBottom: "8px" },
  hint: { fontSize: "12px", color: "#999", margin: 0 },
  saveBtn: { padding: "12px 32px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px" },
};

export default Settings;
